import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { useRouteMatch } from 'react-router-dom';

import { TYPE_OPTS } from 'modules/common/molecules/TabItem/constants';
import { TabItem } from 'modules/common';

function Tab({
  options,
  routeOptions,
  tabItemProps,
  isActiveCondition,
  containerClassName,
  isInsidePageWrapper,
  withBorderBottom,
  withPageWrapperPadding,
}) {
  const parentMatch = useRouteMatch();
  const match = useRouteMatch(`${parentMatch.path}/:navigation_code`);

  const ref = React.useRef();
  const [showFade, setShowFade] = React.useState(false);

  const { navigation_code } = match?.params || {};
  const isRouteOptions =
    Array.isArray(routeOptions) && routeOptions?.length > 0;

  const dispatchIsActive = (row) => {
    if (typeof isActiveCondition === 'function') {
      return isActiveCondition(row);
    }

    if (row?.key === navigation_code && isRouteOptions) {
      return true;
    }

    return row?.isActive || false;
  };

  const handleHasScroll = () => {
    if (!ref.current) return;

    const { scrollWidth, offsetWidth, scrollLeft } = ref.current;

    if (scrollWidth - scrollLeft === offsetWidth) {
      setShowFade(false);
      return;
    }

    setShowFade(true);
  };

  React.useEffect(() => {
    if (!ref.current) return;

    const { scrollWidth, clientWidth } = ref.current;
    const hasScrollbar = scrollWidth > clientWidth;

    if (hasScrollbar) {
      setShowFade(true);
      return;
    }

    setShowFade(false);
  }, []);

  return (
    <div
      className={cn('relative', containerClassName, {
        '-mx-ds-16 lg:-mx-ds-40 px-ds-16 lg:px-ds-40': isInsidePageWrapper,
        'px-ds-16 lg:px-ds-40': withPageWrapperPadding,
        'border-b border-default': withBorderBottom,
      })}
    >
      <div
        className={cn('flex items-center gap-ds-16 no-scrollbar')}
        ref={ref}
        onScroll={handleHasScroll}
      >
        {(isRouteOptions ? routeOptions : options)?.map((item, key) => (
          <TabItem
            // eslint-disable-next-line react/no-array-index-key
            key={key}
            isActive={dispatchIsActive(item)}
            type="default"
            className="flex-shrink-0"
            {...tabItemProps}
            {...item}
            isRouteOptions={isRouteOptions}
            rootUrl={parentMatch?.url}
          />
        ))}
      </div>

      {showFade && (
        <div className="absolute inset-y-0 right-0 h-full w-[55px] bg-gradient-to-l from-[#FAFAFA] to-transparent" />
      )}
    </div>
  );
}

export default Tab;

Tab.defaultProps = {
  options: [],
  tabItemProps: {},
  isActiveCondition: false,
  containerClassName: '',
  isInsidePageWrapper: false,
  withBorderBottom: false,
  routeOptions: false,
  withPageWrapperPadding: false,
};

const PROP_TYPES_TYPE = Object.keys(TYPE_OPTS())?.filter(
  (item) => item !== 'disabled'
);

Tab.propTypes = {
  isActiveCondition: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  isInsidePageWrapper: PropTypes.bool,
  withBorderBottom: PropTypes.bool,
  withPageWrapperPadding: PropTypes.bool,
  routeOptions: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Array),
  ]),
  containerClassName: PropTypes.string,
  tabItemProps: PropTypes.instanceOf(Object),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(PROP_TYPES_TYPE),
      label: PropTypes.string,
      name: PropTypes.string,
      isActive: PropTypes.bool,
      withClose: PropTypes.bool,
      withLeading: PropTypes.bool,
      onClose: PropTypes.bool,
      disabled: PropTypes.bool,
      leadingItem: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.element,
        PropTypes.string,
        PropTypes.instanceOf(Object),
        PropTypes.instanceOf(Function),
      ]),
      withBadge: PropTypes.bool,
      badgeProps: PropTypes.instanceOf(Object),
      onClick: PropTypes.instanceOf(Function),
    })
  ),
};
